const CARRIER_BADGE_TONES = {
  neutral:
    "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-700/60 dark:bg-slate-900/40 dark:text-slate-200",
  yellow:
    "border-yellow-300 bg-yellow-100 text-yellow-900 dark:border-yellow-800/60 dark:bg-yellow-950/40 dark:text-yellow-200",
  amber:
    "border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-200",
  red: "border-red-300 bg-red-100 text-red-900 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-200",
  sky: "border-sky-300 bg-sky-100 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-200",
  blue: "border-blue-300 bg-blue-100 text-blue-900 dark:border-blue-800/60 dark:bg-blue-950/40 dark:text-blue-200",
  emerald:
    "border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-200",
} as const;

type CarrierTone = keyof typeof CARRIER_BADGE_TONES;

const CARRIER_TONE_BY_KEY: Record<string, CarrierTone> = {
  ups: "yellow",
  upgf: "yellow",
  "united parcel service": "yellow",
  estes: "amber",
  exla: "amber",
  "estes express": "amber",
  "estes express lines": "amber",
  aaa: "red",
  aact: "red",
  act: "red",
  "aaa cooper": "red",
  "aaa cooper act": "red",
  fedex: "blue",
  dhl: "red",
  usps: "sky",
};

const CARRIER_FALLBACK_TONES: readonly string[] = [
  CARRIER_BADGE_TONES.sky,
  CARRIER_BADGE_TONES.blue,
  CARRIER_BADGE_TONES.emerald,
  CARRIER_BADGE_TONES.amber,
];

function normalizeCarrierKey(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function pickHashedTone(value: string, tones: readonly string[]): string {
  if (!value) return tones[0]!;
  let hash = 0;
  for (const char of value) {
    hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  }
  return tones[hash % tones.length]!;
}

export function getCarrierBadgeClass(
  carrierName: unknown,
  carrierCode?: unknown,
): string {
  const normalizedName = normalizeCarrierKey(carrierName);
  const normalizedCode = normalizeCarrierKey(carrierCode);
  if (!normalizedName && !normalizedCode) return CARRIER_BADGE_TONES.neutral;

  const exactTone =
    CARRIER_TONE_BY_KEY[normalizedCode] ?? CARRIER_TONE_BY_KEY[normalizedName];
  if (exactTone) return CARRIER_BADGE_TONES[exactTone];

  const combined = `${normalizedCode} ${normalizedName}`.trim();
  if (combined.includes("ups")) return CARRIER_BADGE_TONES.yellow;
  if (combined.includes("estes") || combined.includes("exla")) return CARRIER_BADGE_TONES.amber;
  if (combined.includes("aaa")) return CARRIER_BADGE_TONES.red;
  if (combined.includes("fedex")) return CARRIER_BADGE_TONES.blue;
  if (combined.includes("usps")) return CARRIER_BADGE_TONES.sky;
  if (combined.includes("dhl")) return CARRIER_BADGE_TONES.red;

  return pickHashedTone(normalizedCode || normalizedName, CARRIER_FALLBACK_TONES);
}
